// src/pages/AdminLastCallReservations.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import ForfeoFulfillmentReadinessPanel from '../components/network/ForfeoFulfillmentReadinessPanel';
import {
  FaBolt,
  FaCalendarCheck,
  FaCheckCircle,
  FaClock,
  FaEnvelope,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaRedo,
  FaSearch,
  FaTimesCircle,
  FaUserCheck,
} from 'react-icons/fa';

type ReservationStatus = 'all' | 'confirmed' | 'pending' | 'completed' | 'cancelled' | 'no_show';

type LastCallReservation = {
  id: string;
  status?: string | null;
  admin_status?: string | null;
  public_status?: string | null;
  customer_email?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  party_size?: number | null;
  slot_start?: string | null;
  slot_end?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  cancelled_at?: string | null;
  customer_cancelled_at?: string | null;
  checked_in_at?: string | null;
  no_show_at?: string | null;
  last_confirmation_sent_at?: string | null;
  last_reminder_sent_at?: string | null;
  admin_notes?: string | null;
  message?: string | null;
  total_price?: number | null;
  locked_price?: number | null;
  deal_id?: string | null;
  business_id?: string | null;
  deal?: {
    id?: string;
    title?: string;
    description?: string;
    discount_price?: number | null;
    original_price?: number | null;
    expires_at?: string | null;
  } | null;
  business?: {
    id?: string;
    name?: string;
    region?: string;
    city?: string;
    address?: string;
    phone?: string;
    website?: string;
    email?: string;
  } | null;
};

type ApiStats = {
  total: number;
  confirmed: number;
  pending: number;
  completed: number;
  cancelled: number;
  no_show: number;
  revenue_estimated: number;
};

const statusLabel = (status?: string | null) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('cancel')) return 'Annulée';
  if (s.includes('completed') || s.includes('honor') || s.includes('checked')) return 'Complétée';
  if (s.includes('no_show')) return 'No-show';
  if (s.includes('pending')) return 'À confirmer';
  return 'Confirmée';
};

const statusClass = (status?: string | null) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('cancel')) return 'bg-red-50 text-red-700 border-red-100';
  if (s.includes('completed') || s.includes('honor') || s.includes('checked')) return 'bg-green-50 text-green-700 border-green-100';
  if (s.includes('no_show')) return 'bg-slate-100 text-slate-700 border-slate-200';
  if (s.includes('pending')) return 'bg-amber-50 text-amber-700 border-amber-100';
  return 'bg-blue-50 text-blue-700 border-blue-100';
};

const moneyCad = (value?: number | null) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 2 }).format(n);
};

const dateTime = (value?: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('fr-CA', { dateStyle: 'medium', timeStyle: 'short' }).format(d);
};

const effectiveStatus = (r: LastCallReservation) =>
  String(r.admin_status || r.public_status || r.status || 'confirmed').toLowerCase();

const mapUrl = (r: LastCallReservation) => {
  const b = r.business || {};
  const q = `${b.name || ''} ${b.address || ''} ${b.city || ''} ${b.region || ''}`.trim();
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : '';
};

const getPrice = (r: LastCallReservation) =>
  Number(r.locked_price || r.total_price || r.deal?.discount_price || 0) || 0;

export default function AdminLastCallReservations() {
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [reservations, setReservations] = useState<LastCallReservation[]>([]);
  const [stats, setStats] = useState<ApiStats>({ total: 0, confirmed: 0, pending: 0, completed: 0, cancelled: 0, no_show: 0, revenue_estimated: 0 });
  const [status, setStatus] = useState<ReservationStatus>('all');
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const invokeAdmin = async (body: any) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    const { data, error } = await supabase.functions.invoke('admin-last-call-reservations', {
      body,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (error) throw new Error(error.message || 'Erreur Edge Function');
    if (data?.ok === false) throw new Error(data?.error || 'Action impossible');
    return data;
  };

  const loadReservations = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const data = await invokeAdmin({ action: 'list', status, search });
      setReservations(Array.isArray(data?.reservations) ? data.reservations : []);
      setStats(data?.stats || { total: 0, confirmed: 0, pending: 0, completed: 0, cancelled: 0, no_show: 0, revenue_estimated: 0 });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Chargement impossible' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reservations;
    return reservations.filter((r) => {
      const haystack = [
        r.id,
        r.customer_email,
        r.customer_name,
        r.customer_phone,
        r.deal?.title,
        r.business?.name,
        r.business?.region,
        r.business?.city,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [reservations, search]);

  const runAction = async (reservationId: string, nextAction: string, extra: Record<string, any> = {}) => {
    setActionLoading(`${reservationId}:${nextAction}`);
    setMessage(null);
    try {
      const data = await invokeAdmin({ action: nextAction, reservation_id: reservationId, ...extra });
      setMessage({ type: 'success', text: data?.message || 'Action complétée.' });
      await loadReservations();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Action impossible' });
    } finally {
      setActionLoading(null);
    }
  };

  const askNotesAndUpdate = async (reservationId: string, nextStatus: string) => {
    const notes = window.prompt('Note interne optionnelle pour ce changement :') || '';
    await runAction(reservationId, 'update_status', { status: nextStatus, admin_notes: notes });
  };

  const exportCsv = () => {
    const rows = filtered.map((r) => ({
      id: r.id,
      statut: statusLabel(effectiveStatus(r)),
      client: r.customer_name || '',
      email: r.customer_email || '',
      telephone: r.customer_phone || '',
      personnes: r.party_size || '',
      offre: r.deal?.title || '',
      partenaire: r.business?.name || '',
      prix: getPrice(r),
      date: r.slot_start || r.created_at || '',
    }));
    const header = Object.keys(rows[0] || { id: '', statut: '', client: '', email: '', telephone: '', personnes: '', offre: '', partenaire: '', prix: '', date: '' });
    const csv = [header.join(','), ...rows.map((row: any) => header.map((key) => `"${String(row[key] ?? '').replaceAll('"', '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `forfeo-last-call-reservations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statCards = [
    { label: 'Total', value: stats.total, icon: <FaCalendarCheck />, className: 'bg-slate-900 text-white' },
    { label: 'Confirmées', value: stats.confirmed, icon: <FaCheckCircle />, className: 'bg-blue-50 text-blue-700' },
    { label: 'Complétées', value: stats.completed, icon: <FaUserCheck />, className: 'bg-green-50 text-green-700' },
    { label: 'Annulées', value: stats.cancelled, icon: <FaTimesCircle />, className: 'bg-red-50 text-red-700' },
  ];

  return (
    <div className="animate-in fade-in space-y-6">
      <div className="rounded-[2rem] bg-gradient-to-br from-slate-950 via-slate-900 to-red-950 p-6 text-white shadow-2xl overflow-hidden relative">
        <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-red-500/20 blur-3xl" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.24em] text-red-100">
              <FaBolt /> Last Call 2.0
            </div>
            <h2 className="text-3xl font-black tracking-tight md:text-4xl">Centre réservations Last Call</h2>
            <p className="mt-2 max-w-2xl text-sm font-medium leading-6 text-slate-300">
              Suivi des réservations, statuts, renvois de confirmation, annulations, no-show et export opérationnel.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={loadReservations} className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 transition hover:-translate-y-0.5">
              <FaRedo /> Actualiser
            </button>
            <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-black text-white transition hover:-translate-y-0.5 hover:bg-white/15">
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <ForfeoFulfillmentReadinessPanel
        context="last_call"
        total={stats.total}
        confirmed={stats.confirmed}
        completed={stats.completed}
        anomalies={(stats.pending || 0) + (stats.no_show || 0)}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {statCards.map((card) => (
          <div key={card.label} className={`rounded-3xl border border-slate-100 p-5 shadow-sm ${card.className}`}>
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-white/20 text-xl">{card.icon}</div>
            <div className="text-3xl font-black">{card.value}</div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-70">{card.label}</div>
          </div>
        ))}
        <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Revenus estimés</div>
          <div className="mt-2 text-3xl font-black text-slate-900">{moneyCad(stats.revenue_estimated)}</div>
          <div className="mt-1 text-xs font-semibold text-slate-500">Prix verrouillés Last Call</div>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-center">
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <FaSearch className="text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') loadReservations(); }}
              placeholder="Rechercher par client, courriel, offre ou partenaire..."
              className="w-full bg-transparent text-sm font-bold text-slate-700 outline-none"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              ['all', 'Toutes'],
              ['confirmed', 'Confirmées'],
              ['pending', 'À confirmer'],
              ['completed', 'Complétées'],
              ['cancelled', 'Annulées'],
              ['no_show', 'No-show'],
            ].map(([id, label]) => (
              <button
                key={id}
                onClick={() => setStatus(id as ReservationStatus)}
                className={`rounded-2xl px-4 py-3 text-[11px] font-black uppercase tracking-widest transition ${status === id ? 'bg-slate-950 text-white shadow-lg' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {message && (
        <div className={`rounded-2xl border p-4 text-sm font-bold ${message.type === 'success' ? 'border-green-100 bg-green-50 text-green-700' : 'border-red-100 bg-red-50 text-red-700'}`}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="rounded-3xl bg-white p-12 text-center text-slate-500 shadow-sm">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-red-600" />
          Chargement des réservations Last Call...
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-3xl bg-white p-12 text-center shadow-sm">
          <FaCalendarCheck className="mx-auto mb-4 text-5xl text-slate-300" />
          <h3 className="text-2xl font-black text-slate-900">Aucune réservation trouvée</h3>
          <p className="mt-2 text-sm font-semibold text-slate-500">Change le filtre ou lance une nouvelle recherche.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          {filtered.map((r) => {
            const s = effectiveStatus(r);
            const maps = mapUrl(r);
            const price = getPrice(r);
            return (
              <article key={r.id} className="overflow-hidden rounded-[1.75rem] border border-slate-100 bg-white shadow-sm transition hover:shadow-xl">
                <div className="border-b border-slate-100 p-5">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div>
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-widest ${statusClass(s)}`}>{statusLabel(s)}</span>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-slate-500">{r.party_size || 1} pers.</span>
                      </div>
                      <h3 className="text-xl font-black text-slate-950">{r.deal?.title || r.message || 'Offre Last Call'}</h3>
                      <p className="mt-1 text-xs font-semibold text-slate-500">Réservation #{String(r.id).slice(0, 8)} · {dateTime(r.slot_start || r.created_at)}</p>
                    </div>
                    <div className="text-left md:text-right">
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Prix verrouillé</div>
                      <div className="text-2xl font-black text-red-600">{moneyCad(price)}</div>
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 p-5 md:grid-cols-2">
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <div className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-400">Client</div>
                    <div className="font-black text-slate-900">{r.customer_name || 'Client Forfeo'}</div>
                    <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-slate-500"><FaEnvelope /> {r.customer_email || '—'}</div>
                    {r.customer_phone && <div className="mt-1 text-sm font-semibold text-slate-500">{r.customer_phone}</div>}
                  </div>

                  <div className="rounded-2xl bg-slate-50 p-4">
                    <div className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-400">Partenaire révélé</div>
                    <div className="font-black text-slate-900">{r.business?.name || 'Partenaire Forfeo'}</div>
                    <div className="mt-1 flex items-start gap-2 text-sm font-semibold text-slate-500"><FaMapMarkerAlt className="mt-1" /> <span>{[r.business?.address, r.business?.city, r.business?.region].filter(Boolean).join(', ') || '—'}</span></div>
                    {maps && <a href={maps} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-xs font-black text-blue-700 hover:underline">Ouvrir Google Maps <FaExternalLinkAlt /></a>}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 border-t border-slate-100 p-5">
                  <button disabled={actionLoading !== null} onClick={() => runAction(r.id, 'resend_confirmation')} className="rounded-xl bg-blue-50 px-4 py-2 text-xs font-black text-blue-700 transition hover:bg-blue-100 disabled:opacity-50">
                    {actionLoading === `${r.id}:resend_confirmation` ? 'Envoi...' : 'Renvoyer courriel'}
                  </button>
                  <button disabled={actionLoading !== null} onClick={() => askNotesAndUpdate(r.id, 'completed')} className="rounded-xl bg-green-50 px-4 py-2 text-xs font-black text-green-700 transition hover:bg-green-100 disabled:opacity-50">
                    Marquer complétée
                  </button>
                  <button disabled={actionLoading !== null} onClick={() => askNotesAndUpdate(r.id, 'no_show')} className="rounded-xl bg-slate-100 px-4 py-2 text-xs font-black text-slate-700 transition hover:bg-slate-200 disabled:opacity-50">
                    No-show
                  </button>
                  <button disabled={actionLoading !== null} onClick={() => askNotesAndUpdate(r.id, 'cancelled')} className="rounded-xl bg-red-50 px-4 py-2 text-xs font-black text-red-700 transition hover:bg-red-100 disabled:opacity-50">
                    Annuler
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
